/* ==========================================================================
   lumi — behaviour.                                        assets/theme.js

   Built on two vendored libraries rather than hand-rolled equivalents:

     Motion (MIT, motion.dev)  — inView / animate / stagger / scroll.
       Replaces a bespoke IntersectionObserver + rAF loop. It composes
       independent transforms (translate, scale, rotate) without the
       clobbering that plain `transform` strings cause, drives the Web
       Animations API off the main thread where the browser allows, and
       handles the reduced-motion and interrupt cases properly.

     Lenis (MIT, darkroom.engineering) — damped scrolling. This is the
       single largest contributor to the "expensive" feel on studio sites:
       the page carries momentum instead of snapping a fixed number of
       pixels per wheel tick. Off by default under reduced-motion, and
       disableable from the theme editor.

   Both are self-hosted in assets/. No build step, no CDN request.
   Everything degrades: if either global is missing this file still runs.
   ========================================================================== */
(function () {
  'use strict';

  var doc  = document;
  var root = doc.documentElement;
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var RM = reduceMQ.matches;

  /* iOS Safari never fires :active on a tap unless a touch listener exists
     somewhere in the ancestor chain. Every :active press state in theme.css
     (.size, .btn, .nav .links a, .cart-remove) depends on this one line to
     work on an iPhone. Passive, does nothing itself — do not remove it just
     because it looks like dead code. */
  doc.addEventListener('touchstart', function () {}, { passive: true });

  var M = window.Motion || null;          /* may be absent — always guard */
  var hasMotion = !!(M && M.animate && M.inView);

  function each(l, f) { Array.prototype.forEach.call(l, f); }
  function $(s, c) { return (c || doc).querySelectorAll(s); }

  /* lumi's easing, expressed for Motion. Same curves as the stylesheet. */
  var EASE = {
    set:  [0.19, 1, 0.22, 1],
    rise: [0.22, 0.61, 0.36, 1],
    draw: [0.32, 0.72, 0, 1]
  };

  /* ===================================================== 1. DOM PREP ===== */

  function bindShots(scope) {
    each($('figure .shot', scope), function (img) {
      if (img.__lumi) return; img.__lumi = 1;
      var done = function () {
        if (img.naturalWidth > 1) img.closest('figure').classList.add('has-shot');
      };
      if (img.complete) done();
      else {
        img.addEventListener('load', done);
        img.addEventListener('error', function () { img.remove(); });
      }
    });
  }

  /* dupMarquee() lived here. The scrolling phrase band was removed from the
     site, so the duplicator, the .sl-live animation governor in theme.css and
     sections/marquee.liquid all went with it. */

  try { bindShots(doc); } catch (e) {}

  /* ============================================ 2. WORD SPLIT (display) ===
     Kept hand-written: Motion has no splitter, and this one walks TEXT
     NODES ONLY so <br>, <em> and Liquid's richtext <span>s all survive.
     Never per-character — that destroys the kerning Cormorant is for. */

  function split(el) {
    if (el.__w) return el.__w;
    var walk = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var texts = [], n, words = [];
    while ((n = walk.nextNode())) if (n.nodeValue.trim()) texts.push(n);
    texts.forEach(function (node) {
      var frag = doc.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(doc.createTextNode(part)); return; }
        var mask = doc.createElement('span'); mask.className = 'lw';
        var word = doc.createElement('i');    word.textContent = part;
        mask.appendChild(word); frag.appendChild(mask); words.push(word);
      });
      node.parentNode.replaceChild(frag, node);
    });
    el.__w = words; return words;
  }

  /* group words into visual lines so a whole line rises as one object */
  function lines(el) {
    var w = el.__w || [];
    if (!w.length) return [];
    var tol = Math.max(4, parseFloat(getComputedStyle(el).fontSize) * 0.45);
    var tops = w.map(function (x) { return x.parentNode.offsetTop; });
    var out = [], cur = [], last = null;
    for (var i = 0; i < w.length; i++) {
      if (last !== null && Math.abs(tops[i] - last) > tol) { out.push(cur); cur = []; }
      cur.push(w[i]); last = tops[i];
    }
    if (cur.length) out.push(cur);
    return out;
  }

  /* ================================================= 3. REVEAL / ENTRANCE ==
     Motion's inView replaces the observer + threshold bookkeeping. One-shot
     by design: this page is a printed sequence and ink does not un-set. */

  /* The ENTRANCE stays on CSS transitions, deliberately.
     Motion drives the Web Animations API, and WAAPI reverts an element to its
     underlying CSS value when the animation finishes. Because the at-rest CSS
     here IS the hidden state, a Motion-driven entrance snapped every word back
     to hidden the instant it landed. CSS owns entrance; Motion owns the things
     CSS cannot do well: scroll progress and interruptible interaction. */
  function playSet(el) {
    var ls = lines(el);
    var gap = 78, t = 0;
    ls.forEach(function (line, i) {
      if (i) { t += gap; gap *= 0.86; }
      line.forEach(function (w) {
        w.style.setProperty('--wd', Math.round(Math.min(t, 300)) + 'ms');
      });
    });
    el.classList.add('is-split');
    /* Force a synchronous style flush so the transition has a start value.
       NOT requestAnimationFrame: rAF never fires in a background tab, which
       would leave the headline permanently hidden. */
    void el.offsetWidth;
    el.classList.add('in');
  }

  function playRise(el) { el.classList.add('in'); }

  function reveal(el) {
    if (el.__played) return; el.__played = true;
    if (el.classList.contains('set')) playSet(el); else playRise(el);
  }

  /* decaying stagger — one gesture with an accent on the first item, rather
     than four metronome beats. Stamped as data-d so CSS and JS agree. */
  function stagger(scope) {
    each($('[data-stagger]', scope), function (group) {
      var cfg = group.getAttribute('data-stagger').split(',');
      var base = parseFloat(cfg[0]) || 90;
      var decay = cfg.length > 1 ? parseFloat(cfg[1]) : 0.8;
      var t = parseFloat(group.getAttribute('data-d') || 0), gap = base;
      each(group.children, function (child, i) {
        if (i) { t += gap; gap *= decay; }
        child.dataset.d = Math.round(t);
        child.style.setProperty('--d', Math.round(t) + 'ms');
      });
    });
  }

  /* ============================================== 4. SCROLL-LINKED LAYER ===
     Motion's scroll() gives a 0→1 progress per element without a bespoke
     rAF loop. Writes ONLY custom properties; the stylesheet decides what
     each section does with --p. */

  function bindScrollProgress() {
    if (!hasMotion || RM || !M.scroll) return;
    each($('.hero,.bleed,.story,.layer .stage,.features,.specsec,.pdp,.ivp'), function (el) {
      M.scroll(
        function (p) { el.style.setProperty('--p', Math.round(p * 1000) / 1000); },
        { target: el, offset: ['start end', 'end start'] }
      );
    });
  }

  /* ==================================================== 5. SMOOTH SCROLL ===
     Lenis. Gated on a theme setting and on reduced-motion, and skipped on
     touch, where the native momentum is already correct and overriding it
     is the fastest way to make a phone feel broken. */

  var lenis = null;
  function initLenis() {
    if (RM || !window.Lenis) return;
    if (root.dataset.smooth === 'off') return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    lenis = new window.Lenis({
      duration: 1.05,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      syncTouch: false
    });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);

    /* in-page anchors go through Lenis so they are damped too */
    each($('a[href^="#"]'), function (a) {
      a.addEventListener('click', function (e) {
        var id = a.getAttribute('href');
        if (id.length < 2) return;
        var t = doc.querySelector(id);
        if (!t) return;
        e.preventDefault();
        lenis.scrollTo(t, { offset: -100 });
      });
    });
  }

  /* ============================================================ 6. NAV ==== */

  function bindNav() {
    var nav = doc.querySelector('.nav');
    if (!nav) return;
    var on = null;
    var set = function (y) {
      var next = y > 40;
      if (next !== on) { on = next; nav.classList.toggle('scrolled', next); }
    };
    set(window.scrollY);
    if (lenis) lenis.on('scroll', function (e) { set(e.scroll); });
    else window.addEventListener('scroll', function () { set(window.scrollY); }, { passive: true });
  }

  /* The <details> itself needs no script: it is a native disclosure and
     stays operable with JS absent. This only adds the closing half — an
     open panel otherwise has no way to dismiss itself, and on a tall page
     that means it can park over whatever the customer scrolls to next. */
  function bindNavMore() {
    var more = doc.querySelector('.nav-more');
    if (!more) return;
    var close = function () { more.open = false; };
    doc.addEventListener('pointerdown', function (e) {
      if (more.open && !more.contains(e.target)) close();
    });
    doc.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !more.open) return;
      close();
      var s = more.querySelector('summary');
      if (s) s.focus();
    });
    if (lenis) lenis.on('scroll', function () { if (more.open) close(); });
    else window.addEventListener('scroll', function () { if (more.open) close(); }, { passive: true });
  }

  /* =========================================================== 7. BOOT ==== */

  function prepare(scope) {
    stagger(scope);
    each($('[data-d]', scope), function (el) {
      el.style.setProperty('--d', parseFloat(el.getAttribute('data-d')) + 'ms');
    });
    each($('.reveal.set', scope), split);          /* split while still hidden */
  }

  function observe(scope) {
    each($('.reveal', scope), function (el) {
      if (el.__bound) return; el.__bound = true;
      if (el.closest('[data-overture]')) return;   /* scheduled, not scrolled */
      if (hasMotion && !RM) {
        M.inView(el, function () { reveal(el); }, { margin: '0px 0px -12% 0px' });
      } else {
        reveal(el);
      }
    });
  }

  function boot() {
    try {
      prepare(doc);

      if (RM || !hasMotion) {
        each($('.reveal'), function (el) { el.classList.add('is-split', 'in'); });
      } else {
        observe(doc);
        /* the overture is scheduled, not scrolled. rAF never fires in a
           background tab, so race it against a timer or a page opened in a
           background tab shows an empty hero. */
        var played = false;
        var overture = function () {
          if (played) return; played = true;
          each($('[data-overture] .reveal, [data-overture].reveal'), reveal);
        };
        requestAnimationFrame(function () { requestAnimationFrame(overture); });
        setTimeout(overture, 400);
      }

      initLenis();
      bindNav();
      bindNavMore();
      bindScrollProgress();
    } catch (err) {
      root.classList.add('no-motion');
      if (window.console) console.warn('[lumi] init failed:', err);
    }
    clearTimeout(window.__lumiWD);
  }

  /* Cormorant decides where the hero's lines break, so measure after it
     lands, but never hold the page hostage to a font CDN. Hard 250ms cap. */
  var fonts = (doc.fonts && doc.fonts.load)
    ? Promise.all([doc.fonts.load('500 1em Cormorant'), doc.fonts.load('italic 400 1em Cormorant')])
    : Promise.resolve();
  Promise.race([fonts, new Promise(function (r) { setTimeout(r, 250); })]).then(boot, boot);

  /* ====================================================== 8. LISTENERS ==== */

  doc.addEventListener('visibilitychange', function () {
    root.classList.toggle('tab-hidden', doc.hidden);
  });

  /* Shopify's editor swaps sections in and out live */
  doc.addEventListener('shopify:section:load', function (e) {
    try {
      bindShots(e.target); prepare(e.target);
      if (RM || !hasMotion) each($('.reveal', e.target), function (el) { el.classList.add('is-split','in'); });
      else observe(e.target);
    } catch (err) {}
  });

  function onPref() {
    if (!reduceMQ.matches || RM) return;
    RM = true;
    if (lenis) { lenis.destroy(); lenis = null; }
    each($('.reveal'), function (el) { el.classList.add('is-split', 'in'); });
  }
  if (reduceMQ.addEventListener) reduceMQ.addEventListener('change', onPref);
  else if (reduceMQ.addListener) reduceMQ.addListener(onPref);

  /* ============================================ 9. COMMERCE (own scope) ===
     Last and independently guarded: a motion bug must never stop someone
     buying the skirt. */
  try {
    var live = doc.getElementById('lumi-live');
    function say(m) { if (live) live.textContent = m; }

    /* Every .news-field on the page, not the first one. This was
       querySelector: correct while the newsletter was the only field on the
       site, wrong the moment the contact page put three of them in one form —
       only the name field would have flicked on an invalid submit. */
    each($('.news-field'), function (field) {
      var nform = field.closest('form');
      if (!nform) return;
      if (nform.querySelector('.news-msg.is-bad')) field.classList.add('has-error');
      nform.addEventListener('submit', function () {
        var input = field.querySelector('input, textarea');
        if (input && input.checkValidity && !input.checkValidity()) {
          field.classList.remove('has-error'); void field.offsetWidth;
          field.classList.add('has-error');
        }
      });
      field.addEventListener('input', function () { field.classList.remove('has-error'); });
    });

    /* PDP carousel. The track is a native scroll-snap container, so it already
       swipes and scrolls with this script off. This adds the previous / next
       buttons, the counter and arrow-key stepping, and reveals them. The
       position is always read from the scroll offset, never kept as a second
       copy of the truth, so a swipe, a button and a key can never disagree. */
    each(doc.querySelectorAll('[data-carousel]'), function (car) {
      var track = car.querySelector('[data-car-track]');
      var ui = car.querySelector('[data-car-ui]');
      if (!track || !ui) return;
      var n = track.children.length;
      var prev = ui.querySelector('[data-car-prev]');
      var next = ui.querySelector('[data-car-next]');
      var count = ui.querySelector('[data-car-count]');
      var cur = 0, queued = false;
      function index() {
        return Math.max(0, Math.min(n - 1, Math.round(track.scrollLeft / track.clientWidth)));
      }
      function paint() {
        cur = index();
        count.textContent = (cur + 1) + ' / ' + n;
        prev.disabled = cur === 0;
        next.disabled = cur === n - 1;
      }
      function go(i) {
        i = Math.max(0, Math.min(n - 1, i));
        track.scrollTo({ left: i * track.clientWidth, behavior: RM ? 'auto' : 'smooth' });
      }
      track.addEventListener('scroll', function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () { queued = false; paint(); });
      }, { passive: true });
      prev.addEventListener('click', function () { go(cur - 1); });
      next.addEventListener('click', function () { go(cur + 1); });
      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(cur + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); go(cur - 1); }
      });
      window.addEventListener('resize', function () { track.scrollLeft = cur * track.clientWidth; });
      ui.hidden = false;
      paint();
    });

    var form = doc.querySelector('form.buy-form');
    if (form) {
      var hidden = form.querySelector('input[name="id"]');
      var addBtn = form.querySelector('[data-add]');
      var picked = doc.querySelector('[data-size-picked]');
      var group  = form.querySelector('.sizes');

      /* The sticky bar is a VIEW of this form, not a second form. Both chip
         sets are collected into one list and one selectSize() renders to all
         of them, so the bar and the main block cannot disagree about size. */
      var bar    = doc.querySelector('[data-buybar]');
      var barAdd = bar ? bar.querySelector('[data-bar-add]') : null;
      var btns   = Array.prototype.slice.call(form.querySelectorAll('.size'))
                     .concat(bar ? Array.prototype.slice.call(bar.querySelectorAll('.size')) : []);
      var barGroup = bar ? bar.querySelector('.buybar-sizes') : null;

      if (group) { group.setAttribute('role', 'group'); group.setAttribute('aria-label', 'Size'); }

      /* One writer for the visible status line. Kind is a semantic word, not a
         colour, so the stylesheet owns what "bad" looks like and the copy here
         never has to change if the palette does. Passing no message clears it,
         which is what a fresh interaction should do. */
      /* The bar only appears when the real buy block is OFF screen, so applying
         state to addBtn alone meant a customer who tapped the sticky bar got
         their confirmation painted onto a button they could not see. Every state
         change now goes to both buttons through here. */
      function setBtnState(cls) {
        [addBtn, barAdd].forEach(function (b) {
          if (!b) return;
          b.classList.remove('is-adding', 'is-added', 'is-bad');
          if (cls) b.classList.add(cls);
        });
      }

      var statusEl  = form.querySelector('[data-buy-status]');
      var statusMsg = statusEl ? (statusEl.querySelector('[data-buy-msg]') || statusEl) : null;
      var bagLink   = statusEl ? statusEl.querySelector('[data-buy-bag]') : null;
      /* withBag is only ever true after a real add: the link is the next step for
         someone who has just put the skirt in a bag for the first time. */
      function setStatus(kind, message, withBag) {
        if (!statusEl) return;
        statusEl.classList.remove('is-ok', 'is-bad', 'is-warn');
        statusMsg.textContent = message || '';
        if (bagLink) bagLink.hidden = !withBag;
        if (message) statusEl.classList.add('is-' + kind);
      }

      /* The measurements for the picked size, read from the sizing page's own
         table so there is one set of numbers to keep right. Everything here is
         additive: no fetch, no parse, no row, and the strip stays hidden while
         the "Size & fit" link beside it does the same job. textContent only,
         never innerHTML, since the source is markup we did not write here. */
      var fit = form.querySelector('[data-fit]');
      var fitData = null, fitAsked = false, chosen = '';
      function fitRender() {
        if (!fit || !fitData) return;
        while (fit.firstChild) fit.removeChild(fit.firstChild);
        var row = chosen && fitData.rows[chosen.toLowerCase()];
        if (!row) return;
        var parts = [];
        fitData.cols.forEach(function (c, i) { if (row[i]) parts.push(c + ' ' + row[i]); });
        if (!parts.length) return;
        var line = doc.createElement('span'); line.className = 'fit-line';
        parts.forEach(function (t) {
          var pair = doc.createElement('span'); pair.textContent = t;
          line.appendChild(pair);
        });
        fit.appendChild(line);
        if (fitData.unit) {
          var unit = doc.createElement('span'); unit.className = 'fit-unit'; unit.textContent = fitData.unit;
          fit.appendChild(unit);
        }
      }
      function fitLoad() {
        var src = fit && fit.getAttribute('data-fit-src');
        if (!src || fitAsked || !window.fetch || !window.DOMParser) return;
        fitAsked = true;
        fetch(src, { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw 0; return r.text(); })
          .then(function (html) {
            var t = new DOMParser().parseFromString(html, 'text/html').querySelector('table.chart');
            if (!t) return;
            var cell = function (n) { return n.textContent.trim(); };
            var rows = {};
            each(t.querySelectorAll('tbody tr'), function (tr) {
              var k = tr.querySelector('th');
              if (k) rows[cell(k).toLowerCase()] = Array.prototype.map.call(tr.querySelectorAll('td'), cell);
            });
            var cap = t.querySelector('caption');
            fitData = { rows: rows, unit: cap ? cell(cap) : '',
                        cols: Array.prototype.map.call(t.querySelectorAll('thead th'), cell).slice(1) };
            fit.hidden = false;
            fitRender();
          })
          .catch(function () {});
      }

      function sizeOf(b) { return (b.dataset.size || b.textContent).trim(); }

      /* Single source of truth: one selected size, rendered everywhere. */
      function selectSize(value) {
        chosen = value;
        [group, barGroup].forEach(function (g) { if (g) g.classList.remove('is-asking'); });
        if (statusEl && statusEl.classList.contains('is-warn')) setStatus(null, '');
        fitLoad(); fitRender();
        btns.forEach(function (b) {
          var on = sizeOf(b) === value && !b.hasAttribute('disabled');
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
          if (on && b.dataset.variant && hidden) hidden.value = b.dataset.variant;
        });
        if (picked && picked.textContent.trim() !== value) {
          if (hasMotion && !RM) {
            /* The label is purchase-path state, so the text swap must not depend
               on an animation resolving. A backgrounded tab pauses WAAPI and
               .finished never settles, which would strand the label on the old
               size while the chip reads selected. setTimeout still fires there
               (clamped, not cancelled), so it is the one that guarantees the
               swap; whichever runs first wins and swap() no-ops on the second. */
            var swap = function () {
              if (picked.textContent.trim() === value) return;
              picked.textContent = value;
              M.animate(picked, { opacity: [0, 1], y: [4, 0] }, { duration: 0.22, easing: EASE.set });
            };
            M.animate(picked, { opacity: [1, 0], y: [0, -4] }, { duration: 0.17, easing: EASE.rise })
             .finished.then(swap);
            setTimeout(swap, 200);
          } else { picked.textContent = value; }
        }
        [addBtn, barAdd].forEach(function (b) {
          if (!b) return;
          b.removeAttribute('disabled');
          b.classList.remove('is-added', 'is-adding');
        });
      }

      /* Two different starting states have to be reconciled here.

         On the product template Liquid pre-selects a variant, so the form ships
         with one chip already .is-active and the hidden input already filled.
         The bar's chips are rendered by a snippet that knows nothing about that
         choice, so without this the form would read "XS" while the bar read
         nothing: the same desync the single-source-of-truth design exists to
         prevent. Replaying the selection through selectSize() renders it to
         both sets from the one code path.

         With no variant pre-selected (the homepage, and now the product page) the
         buttons stay LIVE rather than greying out. A disabled button on a phone
         says nothing, and a first-time customer does not know why it will not
         answer; pressed, it now tells them (askForSize below). They are disabled
         only when there is genuinely nothing to buy: every chip sold out. */
      /* :not([disabled]) matters. When every size is sold out Liquid still marks
         a chip .is-active (it is the selected variant) but that chip is disabled
         and the submit button ships disabled with a "Sold out" label. Replaying
         it through selectSize() would call removeAttribute('disabled') and hand
         back a live Add button for a product that cannot be bought. */
      var preselected = form.querySelector('.size.is-active:not([disabled])');
      if (preselected) {
        selectSize(sizeOf(preselected));
      } else if (!hidden.value && !form.querySelector('.size:not([disabled])')) {
        [addBtn, barAdd].forEach(function (b) { if (b) b.setAttribute('disabled', ''); });
      }
      fit && setTimeout(fitLoad, 600);

      /* Add pressed with no size picked. Say so where the customer is looking
         (the sticky bar when it is showing, the form otherwise), flick the chips
         and put focus on the first one that can be chosen. The chips keep a
         darker edge until a size is picked, so the reduced-motion path, which
         drops the flick, still shows what was missing. */
      function askForSize() {
        var barOn = !!(bar && bar.classList.contains('is-on') && barGroup);
        [group, barGroup].forEach(function (g) {
          if (!g) return;
          g.classList.remove('is-asking'); void g.offsetWidth; g.classList.add('is-asking');
        });
        setStatus('warn', 'Choose a size.');
        say('Choose a size.');
        var first = (barOn ? barGroup : group || barGroup);
        first = first && first.querySelector('.size:not([disabled])');
        if (first) first.focus();
      }
      if (barAdd) barAdd.addEventListener('click', function () {
        if (barAdd.hasAttribute('disabled')) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit(addBtn || undefined);
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });

      /* When the bar shows. Three conditions, all required:
           1. the hero has scrolled away  (before that, the page's own CTA is
              right there and a bar would be noise),
           2. the real buy block is NOT on screen (a sticky bar that covers the
              very controls it mirrors is worse than no bar), and
           3. the footer is NOT on screen (on short phones the bar otherwise
              caps the page's own closing signature for the rest of the
              scroll — the same reasoning as condition 2, mirrored at the
              other end of the page).
         Plain IntersectionObserver, not Motion: this is the purchase path and
         must survive the animation library being absent or failing. */
      if (bar && 'IntersectionObserver' in window) {
        var heroEl   = doc.querySelector('.hero');
        var buyEl    = form.querySelector('.buy') || form;
        var footerEl = doc.querySelector('.footer');
        var heroOut = false, buyOut = true, footerOut = true;
        var sync = function () { bar.classList.toggle('is-on', heroOut && buyOut && footerOut); };

        if (heroEl) new IntersectionObserver(function (es) {
          heroOut = !es[0].isIntersecting; sync();
        }, { rootMargin: '-40px 0px 0px 0px' }).observe(heroEl);

        new IntersectionObserver(function (es) {
          buyOut = !es[0].isIntersecting; sync();
        }, { rootMargin: '0px 0px -80px 0px' }).observe(buyEl);

        if (footerEl) new IntersectionObserver(function (es) {
          footerOut = !es[0].isIntersecting; sync();
        }, { rootMargin: '0px 0px -40px 0px' }).observe(footerEl);
      }

      btns.forEach(function (btn, i) {
        btn.type = 'button';
        btn.setAttribute('aria-pressed', btn.classList.contains('is-active') ? 'true' : 'false');
        /* Two chip groups (the in-page form's .sizes and the bar's own
           .buybar-sizes), one flattened array for selectSize() to iterate.
           That flattening was also deciding tab order: `i === 0` only ever
           matches btns[0], which concat() always puts in the FORM's group —
           the bar's chips never got a default-focusable chip of their own,
           so a keyboard user landing in the bar (now reachable in 7 tabs
           since the DOM-order fix) had nothing to land ON. Each group needs
           its own "first chip, unless one in this group is already active"
           check, not one check against the combined list. */
        var ownGroup = btn.parentElement;
        var isFirstInGroup = ownGroup && ownGroup.querySelector('.size') === btn;
        btn.tabIndex = btn.classList.contains('is-active') || (isFirstInGroup && ownGroup && !ownGroup.querySelector('.size.is-active')) ? 0 : -1;

        btn.addEventListener('pointerdown', function (e) {
          var r = btn.getBoundingClientRect();
          var pct = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
          btn.style.setProperty('--fx-origin', pct.toFixed(1) + '%');
        });

        btn.addEventListener('click', function () {
          if (btn.hasAttribute('disabled')) return;
          selectSize(sizeOf(btn));      /* one path for both control sets */
        });

        btn.addEventListener('keydown', function (e) {
          var d = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1
                : (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')  ? -1 : 0;
          if (!d) return;
          e.preventDefault();
          var start = btns.indexOf(btn), j = start, n = btns.length;
          do { j = (j + d + n) % n; } while (btns[j].hasAttribute('disabled') && j !== start);
          btns[j].focus(); btns[j].click();
        });
      });

      form.addEventListener('submit', function (e) {
        /* Always preventDefault, including the no-size case. Falling through to
           a native submit would POST an empty id and drop the customer on a
           Shopify error page. With no size picked the button is still live, so
           this is where it answers. */
        e.preventDefault();
        if (!hidden) return;
        if (!hidden.value) { askForSize(); return; }
        var size = form.querySelector('.size.is-active');
        setBtnState('is-adding');
        addBtn.setAttribute('disabled', '');
        if (barAdd) barAdd.setAttribute('disabled', '');
        setStatus(null, '');
        say('Adding to bag.');

        var rootUrl = (window.Shopify && window.Shopify.routes) ? window.Shopify.routes.root : '/';
        fetch(rootUrl + 'cart/add.js', {
          method: 'POST', body: new FormData(form), headers: { 'Accept': 'application/json' }
        })
        .then(function (r) {
          if (r.ok) return r.json();
          /* Shopify answers a refused add (422) with a sentence that says why,
             e.g. "All 3 are in your cart." Retrying can never fix that, so the
             sentence is surfaced instead of a generic "try again". */
          return r.json().catch(function () { return {}; }).then(function (b) {
            var e = new Error('add refused'); e.detail = b && b.description; throw e;
          });
        })
        /* The item is already in the bag by here. A failure of this second,
           read-only request must not be reported as a failed add, or the
           customer adds it again and buys two. It only costs the count. */
        .then(function () {
          return fetch(rootUrl + 'cart.js', { headers: { 'Accept': 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; });
        })
        .then(function (cart) {
          setBtnState('is-added');
          var n = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
          var bag = doc.querySelector('.bag-count');
          if (bag && n !== null) {
            bag.textContent = '(' + n + ')';
            var link = doc.querySelector('[data-bag-link]');
            if (link) link.setAttribute('aria-label', 'Bag, ' + n + (n === 1 ? ' item' : ' items'));
            if (hasMotion && !RM) {
              M.animate(bag, { scale: [1, 1.28, 1] }, { duration: 0.6, easing: EASE.draw });
            }
          }
          setStatus('ok', 'Added to bag' + (size ? ', size ' + sizeOf(size) : '') + '.', true);
          say('Added to bag' + (size ? ', size ' + sizeOf(size) : '') + '.' +
              (n !== null ? ' ' + n + ' in bag.' : ''));
          setTimeout(function () {
            setBtnState(null);
            addBtn.removeAttribute('disabled');
            if (barAdd) barAdd.removeAttribute('disabled');
          }, 4000);   /* the receipt and its bag link stay until the next add */
        })
        .catch(function (err) {
          /* Failure has to be visible on whichever control was actually used, so
             it tints both. Cleared on a timer rather than left on, because a
             permanently red button reads as a broken product, not a failed try. */
          var msg = (err && err.detail) || 'Could not add to bag. Please try again.';
          setBtnState('is-bad');
          addBtn.removeAttribute('disabled');
          if (barAdd) barAdd.removeAttribute('disabled');
          setTimeout(function () { setBtnState(null); }, 3200);
          setStatus('bad', msg);
          say(msg);
        });
      });
    }
  } catch (e) { if (window.console) console.warn('[lumi] commerce init failed:', e); }
})();
